import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { MoreFromBox } from "@/components/MoreFromBox";
import { BUNDLES } from "@/data/bundles";
import {
  Heart,
  ShoppingBag,
  Minus,
  Plus,
  Truck,
  BadgeIndianRupee,
  Sparkles,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import { PdpAddToBox } from "@/components/PdpAddToBox";
import { toast } from "sonner";
import {
  SHOPIFY_CONFIGURED,
  fetchProductByHandle,
  fetchProducts,
} from "@/lib/shopify";

const PdpSkeleton = () => (
  <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
      <div className="lg:col-span-7">
        <div className="aspect-square rounded-[24px] bg-pearl/60 animate-pulse mb-4" />
        <div className="flex gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="w-20 h-20 rounded-xl bg-pearl/60 animate-pulse" />
          ))}
        </div>
      </div>
      <div className="lg:col-span-5 space-y-4">
        <div className="h-4 w-24 bg-pearl/60 animate-pulse rounded-full" />
        <div className="h-10 w-3/4 bg-pearl/60 animate-pulse rounded-full" />
        <div className="h-6 w-1/3 bg-pearl/60 animate-pulse rounded-full" />
        <div className="h-24 bg-pearl/60 animate-pulse rounded-2xl" />
        <div className="h-12 bg-pearl/60 animate-pulse rounded-full" />
      </div>
    </div>
  </div>
);

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const boxSlug = params.get("box");
  const { addToCart, toggleWishlist, wishlist, cartSyncing } = useStore();
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);

  const [shopifyProduct, setShopifyProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(SHOPIFY_CONFIGURED);
  const [error, setError] = useState(null);

  // Mock fallback (only used if Shopify isn't configured)
  const mockProduct = PRODUCTS.find((p) => p.id === id);

  useEffect(() => {
    if (!SHOPIFY_CONFIGURED) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setActiveImg(0);
    setQty(1);

    fetchProductByHandle(id)
      .then(async (prod) => {
        if (cancelled) return;
        setShopifyProduct(prod);
        setError(null);
        // Load related from same category if we can
        if (prod) {
          try {
            const more = await fetchProducts({ first: 8 });
            if (!cancelled) {
              setRelated(more.filter((p) => p.id !== prod.id).slice(0, 4));
            }
          } catch {
            /* ignore related errors */
          }
        }
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [id]);

  const product = shopifyProduct || mockProduct;

  if (loading && !product) return <PdpSkeleton />;

  if (!product) {
    return (
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24 text-center">
        <p className="font-serif text-3xl text-plum italic mb-4">Piece not found</p>
        <Link to="/shop" className="btn-outline">
          Back to shop
        </Link>
      </div>
    );
  }

  const inWishlist = wishlist.includes(product.id);
  const discount =
    product.mrp && product.mrp > product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : 0;

  // Render either Shopify HTML description or fall back to plain text
  const descriptionBlock = product.descriptionHtml ? (
    <div
      className="text-plum/70 leading-relaxed text-[15px] mb-6 prose prose-sm max-w-none prose-headings:font-serif prose-headings:text-plum prose-p:text-plum/75"
      dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
    />
  ) : (
    <p className="text-plum/70 leading-relaxed text-[15px] mb-6">
      {product.description ||
        "A handpicked GirlyGrove piece — crafted in hypoallergenic, tarnish-free finish for everyday luxury."}
    </p>
  );

  const images = product.images?.length
    ? product.images
    : product.image
      ? [product.image]
      : [];

  const handleAdd = async () => {
    await addToCart(product, qty);
    toast.success(`${product.name} (×${qty}) added to bag`);
  };

  return (
    <div data-testid="product-detail-page">
      {/* Back + Breadcrumb */}
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pt-6 pb-2 text-[11px] tracking-[0.16em] uppercase text-plum/60 flex items-center gap-2 min-w-0">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(boxSlug ? `/bundle/${boxSlug}` : "/shop"))}
          className="inline-flex items-center gap-1.5 mr-2 pl-2.5 pr-3.5 py-1.5 rounded-full border border-plum/15 text-plum hover:border-plum/40 hover:bg-white/60 transition-colors flex-shrink-0"
          data-testid="pdp-back-btn"
        >
          <ArrowLeft size={13} /> {BUNDLES[boxSlug] ? `Back to ${BUNDLES[boxSlug].name}` : "Back"}
        </button>
        <Link to="/" className="hover:text-blush">
          Home
        </Link>
        <ChevronRight size={11} />
        <Link to="/shop" className="hover:text-blush">
          Shop
        </Link>
        <ChevronRight size={11} />
        <span className="text-plum/80 truncate">{product.name}</span>
      </div>

      <section className="max-w-[1440px] mx-auto px-5 lg:px-10 py-8 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          {/* Image gallery */}
          <div className="lg:col-span-7">
            <div className="aspect-square rounded-[24px] overflow-hidden bg-pearl mb-4 relative">
              {images[activeImg] && (
                <img
                  src={images[activeImg]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="pdp-main-image"
                />
              )}
              {product.badge && (
                <span className="absolute top-5 left-5 text-[11px] tracking-[0.18em] uppercase font-medium px-4 py-1.5 rounded-full bg-cream text-plum">
                  {product.badge}
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 flex-wrap">
                {images.map((src, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImg(idx)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${
                      activeImg === idx
                        ? "border-gold"
                        : "border-transparent"
                    }`}
                    data-testid={`pdp-thumb-${idx}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-5">
            <div className="overline mb-3">{product.category}</div>
            <h1
              className="font-serif text-plum leading-[1] tracking-tight mb-3"
              style={{ fontSize: "clamp(32px, 4.4vw, 52px)" }}
            >
              {product.name}
            </h1>
            <div className="flex items-center gap-3 mb-7">
              <span className="text-2xl text-plum font-medium">
                ₹{product.price}
              </span>
              {product.mrp && product.mrp > product.price && (
                <>
                  <span className="text-plum/40 text-lg line-through">
                    ₹{product.mrp}
                  </span>
                  <span className="text-[12px] text-blush font-medium tracking-wide bg-sectionbreak px-2.5 py-1 rounded-full">
                    {discount}% off
                  </span>
                </>
              )}
            </div>

            {descriptionBlock}

            {product.tags?.length > 0 && (
              <div className="mb-7">
                <p className="overline mb-3">Details</p>
                <div className="flex flex-wrap gap-2">
                  {product.tags.slice(0, 6).map((t) => (
                    <span
                      key={t}
                      className="text-[12px] text-plum/75 px-3 py-1.5 rounded-full border border-gold/30 tracking-wide capitalize"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity + actions */}
            <PdpAddToBox product={product} />
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center border border-gold/40 rounded-full">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-11 h-11 flex items-center justify-center text-plum hover:text-blush"
                  data-testid="qty-minus"
                >
                  <Minus size={14} />
                </button>
                <span className="w-8 text-center font-medium text-plum" data-testid="qty-value">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="w-11 h-11 flex items-center justify-center text-plum hover:text-blush"
                  data-testid="qty-plus"
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={handleAdd}
                disabled={cartSyncing || product.available === false}
                className="btn-primary flex-1 disabled:opacity-60"
                data-testid="pdp-add-to-cart"
              >
                <ShoppingBag size={14} />
                {product.available === false
                  ? "Sold out"
                  : cartSyncing
                    ? "Adding…"
                    : "Add to bag"}
              </button>
            </div>
            <div className="flex items-center gap-3 mb-8">
              <button
                onClick={async () => {
                  await addToCart(product, qty);
                  navigate("/cart");
                }}
                disabled={cartSyncing || product.available === false}
                className="btn-outline flex-1 disabled:opacity-60"
                data-testid="pdp-buy-now"
              >
                Buy now
              </button>
              <button
                onClick={() => {
                  toggleWishlist(product.id);
                  toast.success(
                    inWishlist ? "Removed from wishlist" : "Added to wishlist",
                  );
                }}
                className="w-12 h-12 rounded-full border border-gold/40 flex items-center justify-center text-plum hover:bg-sectionbreak transition-colors"
                aria-label="Wishlist"
                data-testid="pdp-wishlist"
              >
                <Heart
                  size={16}
                  className={inWishlist ? "fill-blush text-blush" : ""}
                />
              </button>
            </div>

            {error && (
              <p className="text-blush text-sm font-medium mb-6">
                Couldn't load full details — showing what we have.
              </p>
            )}

            {/* Trust strip */}
            <div className="grid grid-cols-2 gap-4 pt-6 border-t border-sectionbreak">
              <div className="flex items-center gap-3 text-plum/80 text-[13px]">
                <Truck size={16} className="text-gold" /> Free shipping ₹499+
              </div>
              <div className="flex items-center gap-3 text-plum/80 text-[13px]">
                <BadgeIndianRupee size={16} className="text-gold" /> COD available
              </div>
              <div className="flex items-center gap-3 text-plum/80 text-[13px]">
                <Sparkles size={16} className="text-gold" /> Tarnish-free
              </div>
              <div className="flex items-center gap-3 text-plum/80 text-[13px]">
                <Heart size={16} className="text-gold" /> Hypoallergenic
              </div>
            </div>
          </div>
        </div>
      </section>

      <MoreFromBox product={product} boxSlug={boxSlug} />

      {/* Related */}
      {related.length > 0 && (
        <section
          className="py-16 lg:py-20 border-t border-sectionbreak"
          style={{ background: "#F9F2EC" }}
        >
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
            <h2
              className="font-serif text-plum leading-[1] tracking-tight mb-10"
              style={{ fontSize: "clamp(28px, 3.6vw, 44px)" }}
            >
              You might also <span className="italic text-blush">love</span>
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetailPage;
